import {
	balanceSheetId,
	dataSourceSchema,
	financialPeriodId,
	financialPeriodPreparationMethodSchema,
	incomeStatementId,
	isoDateSchema,
	isoDateTimeSchema,
	periodTypeSchema,
} from "@inrev/common";
import * as z from "zod";
import { listData, listInput } from "../schemas/fragments";
import { layeredTotalSchema } from "../schemas/layers";
import { defineTool } from "../tool-definition";

/**
 * The active statements' own section totals, so a year-over-year series costs one call. Derived
 * figures are deliberately absent: working capital and the profit subtotals are computed, not
 * reported, and belong to whoever is asking rather than to this payload.
 */
const figuresSchema = z.strictObject({
	totalAssets: layeredTotalSchema,
	currentAssets: layeredTotalSchema,
	totalLiabilities: layeredTotalSchema,
	currentLiabilities: layeredTotalSchema,
	equity: layeredTotalSchema,
	revenue: layeredTotalSchema,
});

/**
 * A period can hold multiple statements of one identity and an underwriter marks one active. The others are
 * not merely older: a later package can restate an earlier year, so an alternate may be the better
 * document. `fileName` and `dataSource` are what let the model tell a restatement from a supersede,
 * which is why provenance rides along rather than waiting for `get_financial_statement`.
 */
const statementSlot = <TId extends z.ZodType>(statementId: TId) =>
	z.strictObject({
		active: z
			.strictObject({
				statementId,
				reviewedAt: isoDateTimeSchema.nullable(),
			})
			.nullable(),
		alternates: z.array(
			z.strictObject({
				statementId,
				createdAt: isoDateTimeSchema,
				fileName: z.string().nullable(),
				dataSource: dataSourceSchema.nullable(),
			}),
		),
	});

const periodSchema = z.strictObject({
	financialPeriodId,
	fiscalYear: z.number().int(),
	periodType: periodTypeSchema,
	periodStartDate: isoDateSchema.nullable(),
	periodEndDate: isoDateSchema,
	preparationMethod: financialPeriodPreparationMethodSchema,
	companyName: z.string().nullable(),
	usedInUnderwriting: z.boolean(),
	notUsedReason: z
		.enum(["no_active_statement", "interim_period", "future_period_end", "lower_quality_same_date"])
		.nullable(),
	balanceSheet: statementSlot(balanceSheetId),
	incomeStatement: statementSlot(incomeStatementId),
	figures: figuresSchema,
});

export const listFinancialPeriods = defineTool({
	name: "list_financial_periods",
	description:
		"One row per financial period on this account, ordered newest period end first, then by descending assurance within a period end. A period is one period end, period type and preparation method, so the same fiscal year end can appear as both an audit row and an internal row. `usedInUnderwriting` marks the row the underwriting engine selects for that period end and `notUsedReason` says why a row was not selected; the ranking behind both is the engine's, and `preparationMethod` on each row is the underlying fact. `figures` are the active statements' own section totals, each in the same three layers as `get_financial_statement`, so a series across periods needs one call; working capital and profit subtotals are not computed. Read a period line by line with `get_financial_statement`. A period holds many statements of a kind and an underwriter marks one active; `balanceSheet.alternates` and `incomeStatement.alternates` name the others with the file and `dataSource` they came from, since a later package can restate an earlier year, so an alternate is not necessarily an older draft. `preparationMethod` and `companyName` can differ between periods, meaning assurance level or reporting entity changed across the series.",
	label: "Financial periods",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: listInput({ periodType: periodTypeSchema.optional() }),
	data: listData(periodSchema),
});
